/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Training Assignments API Client
 *
 * API client for interacting with training assignment endpoints.
 * Handles both admin and user-facing assignment operations.
 *
 * Patterns:
 * - Extends AuthenticatedApiClient for token injection
 * - Cached singleton factory for performance
 * - snake_case → camelCase transformation
 * - Comprehensive error handling
 */

import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import {dueDateToIsoEndOfDay} from '@/matic/lib/utils/format-date';
import type {
  AssignTrainingRequest,
  AssignTrainingResult,
  UserProgressList,
  UserProgressSummary,
  ProgressDetail,
} from '@/matic/features/training/assignments/types';
import type {
  MyAssignment,
  MyAssignmentList,
  AssignmentDetail,
  AssignmentStatus,
  DocumentProgress,
  SubmitQuizResult,
  QuizAnswer,
  // AcknowledgeDocumentRequest,
} from '@/matic/features/training/my-training/types';
import type {
  QuizAttemptsList,
  SanitizedQuizDefinition,
} from '@/matic/features/training/programs/types/quiz';

/** ============================================================================
 * Shared Utility Functions
 * ============================================================================ */

/**
 * Map backend assignment status to frontend AssignmentStatus enum
 */
function mapAssignmentStatus(status: string): AssignmentStatus {
  switch (status.toLowerCase()) {
    case 'assigned': {
      return 'pending';
    }

    case 'in_progress': {
      return 'in_progress';
    }

    case 'completed': {
      return 'completed';
    }

    case 'failed': {
      return 'failed';
    }

    case 'overdue': {
      return 'overdue';
    }

    case 'cancelled': {
      return 'cancelled';
    }

    case 'expired': {
      return 'expired';
    }

    default: {
      // Unknown status from backend — log and fall back to ``pending`` so
      // the UI doesn't crash, but at least we'll see in the console when
      // the backend ships a new enum the frontend hasn't been updated for.
      console.warn(`Unknown assignment status from backend: ${status}`);
      return 'pending';
    }
  }
}

/**
 * Calculate completion percentage from completed/total counts
 */
function calculateCompletionPercentage(
  completed: number,
  total: number
): number {
  return total > 0 ? Math.round((completed / total) * 100) : 0;
}

/** ============================================================================
 * API Response Types (snake_case, backend format)
 * ============================================================================ */
type ProgressDetailResponse = {
  progress_id: string;
  document_revision_id: string;
  document_name: string;
  document_version: number;
  document_revision_label?: string | null;
  status: string;
  completed_at?: string;
};

type UserProgressSummaryResponse = {
  user_id: string;
  user_name: string;
  user_email: string;
  assignment_id: string;
  assigned_revision_id: string;
  assigned_at: string;
  /** Always present — every program assignment carries a due date. */
  due_date: string;
  status: string;
  completed_at?: string;
  total_items: number;
  completed_items: number;
  pending_items: number;
  progress_details: ProgressDetailResponse[];

  is_blocked_on_instructor?: boolean;
};

type UserProgressListResponse = {
  program_id: string;
  program_name: string;
  user_progress: UserProgressSummaryResponse[];
};

/** Progress on a single program-item step (nested in assignment response) */
type ProgressItemResponse = {
  progress_id: string;
  // Doc fields nullable on the wire — undefined for SIGNOFF/EVIDENCE rows.
  document_id?: string | null;
  document_revision_id?: string | null;
  document_name?: string | null;
  document_version?: number | null;
  document_revision_label?: string | null;
  status: string;
  completed_at?: string | null;
  display_order?: number | null;
  completion_method?: string;
  program_item_id?: string | null;
  item_type?: string | null;
  item_title?: string | null;
  quiz_definition?: Record<string, unknown> | null;
  attempts_remaining?: number | null;
  attempt_count?: number;
  evidence_label?: string | null;
  expiry_required?: boolean | null;
  secondary_attestation_required?: boolean | null;
  signoff_signer_id?: string | null;
  signoff_signer_name?: string | null;
  signoff_signer_email?: string | null;
  signoff_signed_at?: string | null;
  signoff_reason?: string | null;
  upload_file_id?: string | null;
  upload_filename?: string | null;
  upload_content_type?: string | null;
  uploaded_by_id?: string | null;
  uploaded_by_name?: string | null;
  uploaded_at?: string | null;
  upload_expiry_date?: string | null;
};

/** Response from quiz submission */
type SubmitQuizResponse = {
  attempt_id: string;
  attempt_number: number;
  score: number;
  passed: boolean;
  passing_score: number;
  total_questions: number;
  incorrect_question_ids: string[];
  attempts_remaining: number;
  assignment_status: string;
  created_at: string;
};

/** Response for quiz attempts list */
type QuizAttemptsListResponse = {
  attempts: Array<{
    id: string;
    attempt_number: number;
    score: number;
    passed: boolean;
    created_at: string;
  }>;
  total_attempts: number;
  max_attempts: number;
  best_score: number | null;
  passed: boolean;
};

type MyAssignmentResponse = {
  id: string;
  training_program_id: string;
  training_program_revision_id: string;
  training_program_name: string;
  assigned_user_id: string;
  status: string;
  /** Always present — every program assignment carries a due date. */
  due_date: string;
  completed_at?: string;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  tenant_id: string;
  progress_items?: ProgressItemResponse[];
  source_group_id?: string;
  source_group_name?: string;

  is_blocked_on_instructor?: boolean;
};

type MyAssignmentListResponse = {
  items: MyAssignmentResponse[];
  total: number;
};

/** Response from assignment detail endpoint - same structure as MyAssignmentResponse */
type AssignmentDetailResponse = MyAssignmentResponse;

/**
 * Training Assignments API Client
 *
 * Provides methods for managing training assignments, including:
 * - Admin operations: Assign training to users, fetch progress
 * - User operations: View assignments, acknowledge documents
 */
export class TrainingAssignmentsApiClient extends AuthenticatedApiClient {
  private get basePath(): string {
    return '/api/v1/training';
  }

  /**
   * Assign training program to users and/or a group (Admin)
   *
   * POST /api/v1/training/training-programs/{programId}/revisions/{revisionId}/assignments
   *
   * Returns the envelope shape introduced in Phase 3: created
   * assignments, per-user skipped list (already-active users), and
   * provenance breakdown (via_group vs individual). The mutation hook
   * composes those into a summary toast.
   */
  async assignTraining(
    programId: string,
    revisionId: string,
    request: AssignTrainingRequest
  ): Promise<AssignTrainingResult> {
    const response = await this.request<{
      assignments: Array<{
        id: string;
        assigned_user_id: string;
        source_group_id?: string;
        source_group_name?: string;
      }>;
      skipped: Array<{user_id: string; reason: string}>;
      created_count: {via_group: number; individual: number};
    }>(
      `${this.basePath}/training-programs/${programId}/revisions/${revisionId}/assignments`,
      {
        method: 'POST',
        body: JSON.stringify({
          user_ids: request.userIds,
          ...(request.groupId && {group_id: request.groupId}),
          // Convert the date-only picker value into a local-end-of-day
          // ISO datetime so the backend's ``due_date > created_at``
          // check constraint isn't tripped by users west of UTC who
          // submit late in their local day.
          due_date: dueDateToIsoEndOfDay(request.dueDate),
          ...(request.notes && {notes: request.notes}),
        }),
      }
    );
    return {
      assignments: response.assignments.map((a) => ({
        id: a.id,
        assignedUserId: a.assigned_user_id,
        sourceGroupId: a.source_group_id,
        sourceGroupName: a.source_group_name,
      })),
      skipped: response.skipped.map((s) => ({
        userId: s.user_id,
        reason: s.reason,
      })),
      createdCount: {
        viaGroup: response.created_count.via_group,
        individual: response.created_count.individual,
      },
    };
  }

  /**
   * Get user progress for a training program (Admin)
   *
   * GET /api/v1/training/training-programs/{programId}/user-progress
   *
   * This endpoint returns enriched UserProgressListResponse with:
   * - User names and emails (from identity service)
   * - Pre-calculated metrics (totalItems, completedItems, pendingItems)
   * - Enriched document details (name, version)
   *
   * Optional status filtering: assigned, in_progress, completed, overdue.
   *
   * ``includeArchived`` defaults to false so the admin tab matches the
   * trainee My Training default in hiding cancelled (soft-deleted) rows.
   * The Assignments tab's status filter flips it to true when the admin
   * opts in to "Cancelled" from the column header.
   */
  async getUserProgressForProgram(
    programId: string,
    options: {status?: AssignmentStatus; includeArchived?: boolean} = {}
  ): Promise<UserProgressList> {
    const parameters = new URLSearchParams();
    if (options.status) {
      parameters.append('status', options.status);
    }

    if (options.includeArchived) {
      parameters.append('include_archived', 'true');
    }

    const queryString = parameters.toString();
    const url = queryString
      ? `${this.basePath}/training-programs/${programId}/user-progress?${queryString}`
      : `${this.basePath}/training-programs/${programId}/user-progress`;

    const response = await this.request<UserProgressListResponse>(url);

    return {
      programId: response.program_id,
      programName: response.program_name, // Real name from backend!
      userProgress: response.user_progress.map(
        (item) => this.convertUserProgressSummary(item) // Use existing converter!
      ),
    };
  }

  /**
   * Get user's training assignments (User)
   *
   * GET /api/v1/training/my-trainings
   */
  async getMyAssignments(filters?: {
    status?: string;
  }): Promise<MyAssignmentList> {
    const parameters = new URLSearchParams();
    if (filters?.status) {
      parameters.append('status', filters.status);
    }

    const queryString = parameters.toString();
    const url = queryString
      ? `${this.basePath}/my-trainings?${queryString}`
      : `${this.basePath}/my-trainings`;

    const response = await this.request<MyAssignmentListResponse>(url);

    return {
      items: response.items.map((item) => this.convertMyAssignment(item)),
      total: response.total,
    };
  }

  /**
   * Get details of a specific training assignment (User)
   *
   * GET /api/v1/training/training-assignments/{assignmentId}
   */
  async getAssignmentDetail(assignmentId: string): Promise<AssignmentDetail> {
    const response = await this.request<AssignmentDetailResponse>(
      `${this.basePath}/training-assignments/${assignmentId}`
    );

    return this.convertAssignmentDetail(response);
  }

  /**
   * Acknowledge a document in a training assignment (User)
   *
   * POST /api/v1/training/training-assignments/{assignmentId}/progress/{progressId}/acknowledge
   */
  async acknowledgeDocument(
    assignmentId: string,
    progressId: string
  ): Promise<void> {
    await this.request(
      `${this.basePath}/training-assignments/${assignmentId}/progress/${progressId}/acknowledge`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Submit a quiz attempt for a training assignment progress item
   *
   * POST /api/v1/training/training-assignments/{assignmentId}/progress/{progressId}/submit-quiz
   */
  async submitQuiz(
    assignmentId: string,
    progressId: string,
    answers: QuizAnswer[]
  ): Promise<SubmitQuizResult> {
    const response = await this.request<SubmitQuizResponse>(
      `${this.basePath}/training-assignments/${assignmentId}/progress/${progressId}/submit-quiz`,
      {
        method: 'POST',
        body: JSON.stringify({
          answers: answers.map((a) => ({
            question_id: a.questionId,
            selected_option_id: a.selectedOptionId,
          })),
        }),
      }
    );

    return {
      attemptId: response.attempt_id,
      attemptNumber: response.attempt_number,
      score: response.score,
      passed: response.passed,
      passingScore: response.passing_score,
      totalQuestions: response.total_questions,
      incorrectQuestionIds: response.incorrect_question_ids,
      attemptsRemaining: response.attempts_remaining,
      assignmentStatus: response.assignment_status,
      createdAt: response.created_at,
    };
  }

  /**
   * Permanently delete a training assignment and all related records (Admin)
   *
   * DELETE /api/v1/training/training-assignments/{assignmentId}/hard
   *
   * Destructive: drops the assignment row + cascades to progress and
   * quiz_attempts. Reserved for the failed-attempt escape hatch — the
   * scheduling fan-out FK is RESTRICT, so this can throw on a
   * fanout-linked assignment; caller surfaces the backend error.
   */
  async hardDeleteAssignment(assignmentId: string): Promise<void> {
    await this.delete(
      `${this.basePath}/training-assignments/${assignmentId}/hard`
    );
  }

  /**
   * Cancel (soft-delete) a training assignment (Admin/Quality)
   *
   * DELETE /api/v1/training/training-assignments/{assignmentId}
   *
   * Sets status=cancelled and deleted_at on the assignment row. Progress
   * rows + e-signatures are preserved for audit. Trainee's My Training
   * filter excludes cancelled by default; admin Assignments tab can
   * surface cancelled rows via the ``include_archived`` query param.
   * Backend rejects (422 / domain error) on terminal-state assignments
   * (already completed / failed / expired / cancelled).
   */
  async cancelAssignment(assignmentId: string): Promise<void> {
    await this.delete(`${this.basePath}/training-assignments/${assignmentId}`);
  }

  /**
   * List quiz attempts for a training assignment progress item
   *
   * GET /api/v1/training/training-assignments/{assignmentId}/progress/{progressId}/quiz-attempts
   */
  async listQuizAttempts(
    assignmentId: string,
    progressId: string
  ): Promise<QuizAttemptsList> {
    const response = await this.request<QuizAttemptsListResponse>(
      `${this.basePath}/training-assignments/${assignmentId}/progress/${progressId}/quiz-attempts`
    );

    return {
      attempts: response.attempts.map((a) => ({
        id: a.id,
        attemptNumber: a.attempt_number,
        score: a.score,
        passed: a.passed,
        createdAt: a.created_at,
      })),
      totalAttempts: response.total_attempts,
      maxAttempts: response.max_attempts,
      bestScore: response.best_score,
      passed: response.passed,
    };
  }

  /**
   * Convert snake_case UserProgressSummaryResponse to camelCase UserProgressSummary
   *
   * This handles the enriched user progress endpoint response.
   * Transformation details:
   * - user_id → userId
   * - user_name → userName
   * - user_email → userEmail
   * - assignment_id → assignmentId
   * - assigned_revision_id → assignedRevisionId
   * - assigned_at → assignedAt
   * - due_date → dueDate
   * - total_items → totalItems
   * - completed_items → completedItems
   * - pending_items → pendingItems
   * - progress_details → progressDetails (with nested conversion)
   */
  private convertUserProgressSummary(
    response: UserProgressSummaryResponse
  ): UserProgressSummary {
    return {
      userId: response.user_id,
      userName: response.user_name,
      userEmail: response.user_email,
      assignmentId: response.assignment_id,
      assignedRevisionId: response.assigned_revision_id,
      assignedAt: response.assigned_at,
      dueDate: response.due_date,
      status: response.status as UserProgressSummary['status'],
      completedAt: response.completed_at,
      totalItems: response.total_items,
      completedItems: response.completed_items,
      pendingItems: response.pending_items,
      progressDetails: response.progress_details.map((detail) =>
        this.convertProgressDetail(detail)
      ),
      isBlockedOnInstructor: response.is_blocked_on_instructor ?? false,
    };
  }

  /**
   * Convert snake_case response to camelCase ProgressDetail
   */
  private convertProgressDetail(
    response: ProgressDetailResponse
  ): ProgressDetail {
    return {
      progressId: response.progress_id,
      documentRevisionId: response.document_revision_id,
      documentName: response.document_name,
      documentVersion: response.document_version,
      documentRevisionLabel: response.document_revision_label ?? undefined,
      status: response.status as ProgressDetail['status'],
      completedAt: response.completed_at,
    };
  }

  /**
   * Convert snake_case response to camelCase MyAssignment
   *
   * Calculates derived fields from progress_items:
   * - documentCount: length of progress_items array
   * - acknowledgedCount: count of items with status='completed'
   * - percentageComplete: (acknowledgedCount / documentCount) * 100
   * - trainingProgramName: program revision's name from the content_snapshot (from backend)
   */
  private convertMyAssignment(response: MyAssignmentResponse): MyAssignment {
    // Calculate metrics from progress_items
    const progressItems = response.progress_items ?? [];
    const documentCount = progressItems.length;
    const acknowledgedCount = progressItems.filter(
      (p) => p.status === 'completed'
    ).length;

    return {
      assignmentId: response.id,
      trainingProgramId: response.training_program_id,
      trainingProgramName: response.training_program_name,
      status: mapAssignmentStatus(response.status),
      percentageComplete: calculateCompletionPercentage(
        acknowledgedCount,
        documentCount
      ),
      dueDate: response.due_date,
      assignedAt: response.created_at,
      assignedBy: response.created_by,
      completedAt: response.completed_at,
      documentCount,
      acknowledgedCount,
      isBlockedOnInstructor: response.is_blocked_on_instructor ?? false,
      sourceGroupId: response.source_group_id,
      sourceGroupName: response.source_group_name,
    };
  }

  /**
   * Convert TrainingAssignmentResponse to AssignmentDetail
   *
   * Calculates derived fields from progress_items:
   * - documentCount: length of progress_items array
   * - completedItems: count of items with status='completed'
   * - percentageComplete: (completedItems / documentCount) * 100
   * - trainingProgramName: placeholder since not in assignment response
   *
   * Maps:
   * - id → assignmentId
   * - training_program_id → trainingProgramId
   * - created_at → assignedAt
   * - progress_items → documents (with conversion)
   */
  private convertAssignmentDetail(
    response: AssignmentDetailResponse
  ): AssignmentDetail {
    const progressItems = response.progress_items ?? [];

    // Dedupe by progress_id (always unique). Earlier code keyed on
    // document_revision_id which collapsed all SIGNOFF/EXTERNAL_EVIDENCE
    // rows together (their document_revision_id is null) and silently
    // dropped legitimate items.
    const uniqueProgressItems: ProgressItemResponse[] = [];
    const seenProgressIds = new Set<string>();

    for (const item of progressItems) {
      if (!seenProgressIds.has(item.progress_id)) {
        seenProgressIds.add(item.progress_id);
        uniqueProgressItems.push(item);
      }
    }

    const completedItems = uniqueProgressItems.filter(
      (p) => p.status === 'completed'
    ).length;

    return {
      assignmentId: response.id,
      trainingProgramId: response.training_program_id,
      trainingProgramName: response.training_program_name,
      status: mapAssignmentStatus(response.status),
      percentageComplete: calculateCompletionPercentage(
        completedItems,
        uniqueProgressItems.length
      ),
      dueDate: response.due_date,
      assignedAt: response.created_at,
      completedAt: response.completed_at,
      documents: uniqueProgressItems.map((item) =>
        this.convertDocumentProgress(item)
      ),
      sourceGroupId: response.source_group_id ?? undefined,
      sourceGroupName: response.source_group_name ?? undefined,
    };
  }

  /**
   * Convert ProgressItemResponse to DocumentProgress
   *
   * Transformation details:
   * - document_id → documentId
   * - document_revision_id → documentRevisionId
   * - progress_id → progressId
   * - document_name → documentName (with fallback for missing)
   * - document_version → documentVersion
   * - completed_at → acknowledgedAt
   * - display_order → displayOrder
   */
  private convertDocumentProgress(
    response: ProgressItemResponse
  ): DocumentProgress {
    // ``item_type`` is the canonical source for SIGNOFF/EXTERNAL_EVIDENCE
    // rows (which carry no completion_method on the legacy doc-association
    // path). Fall back to completion_method, then to 'acknowledgement'.
    const completionMethod = (response.item_type ??
      response.completion_method ??
      'acknowledgement') as DocumentProgress['completionMethod'];

    return {
      documentId: response.document_id ?? undefined,
      documentRevisionId: response.document_revision_id ?? undefined,
      progressId: response.progress_id,
      documentName: response.document_name ?? undefined,
      documentVersion: response.document_version ?? undefined,
      documentRevisionLabel: response.document_revision_label ?? undefined,
      status: response.status as DocumentProgress['status'],
      acknowledgedAt: response.completed_at ?? undefined,
      displayOrder: response.display_order ?? 0,
      completionMethod,
      programItemId: response.program_item_id ?? undefined,
      itemTitle: response.item_title ?? undefined,
      ...(response.quiz_definition && {
        quizDefinition: this.convertSanitizedQuizDefinition(
          response.quiz_definition
        ),
      }),
      attemptsRemaining: response.attempts_remaining ?? undefined,
      attemptCount: response.attempt_count,
      evidenceLabel: response.evidence_label ?? undefined,
      expiryRequired: response.expiry_required ?? undefined,
      secondaryAttestationRequired:
        response.secondary_attestation_required ?? undefined,
      signoffSignerId: response.signoff_signer_id ?? undefined,
      signoffSignerName: response.signoff_signer_name ?? undefined,
      signoffSignerEmail: response.signoff_signer_email ?? undefined,
      signoffSignedAt: response.signoff_signed_at ?? undefined,
      signoffReason: response.signoff_reason ?? undefined,
      uploadFileId: response.upload_file_id ?? undefined,
      uploadFilename: response.upload_filename ?? undefined,
      uploadContentType: response.upload_content_type ?? undefined,
      uploadedById: response.uploaded_by_id ?? undefined,
      uploadedByName: response.uploaded_by_name ?? undefined,
      uploadedAt: response.uploaded_at ?? undefined,
      uploadExpiryDate: response.upload_expiry_date ?? undefined,
    };
  }

  /**
   * Convert sanitized quiz definition from backend (no correct answers)
   */
  private convertSanitizedQuizDefinition(
    data: Record<string, unknown>
  ): SanitizedQuizDefinition {
    const d = data as any;
    return {
      passingScore: d.passing_score,
      maxAttempts: d.max_attempts,
      questions: ((d.questions ?? []) as unknown[]).map((q: any) => ({
        id: q.id,
        text: q.text,
        options: ((q.options ?? []) as unknown[]).map((opt: any) => ({
          id: opt.id,
          text: opt.text,
        })),
      })),
    };
  }
}

/**
 * Cached singleton factory for TrainingAssignmentsApiClient
 *
 * Returns the same client instance for the same token,
 * reducing object creation overhead.
 */
const clientCache = new Map<string, TrainingAssignmentsApiClient>();

export function getTrainingAssignmentsApi(
  token?: string
): TrainingAssignmentsApiClient {
  const key = token ?? 'no-token';

  if (!clientCache.has(key)) {
    clientCache.set(key, new TrainingAssignmentsApiClient(token));
  }

  return clientCache.get(key)!;
}
